import styled, {useTheme} from 'styled-components/native';
import React, {useCallback, useState} from 'react';
import Animated, {FadeInDown} from 'react-native-reanimated';
import {
  Platform,
  RefreshControl,
  TouchableOpacity,
  View,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {connect} from 'react-redux';
import {RootState} from '../../redux/store';
import TextGeneric from '../textGeneric';
import {Theme} from '../../theme/lightTheme';
import Icon from '../icon';
import {tr} from '../../translations';
import {EmptyContainer, Space} from '../../screens/content/home';
import SearchListSkeleton from '../searchListSkeleton';
import EmptyList from '../emptyList';
import {useCustomHeaderHeight} from '../../utils/hooks/useHeaderHeight';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import {isWeb} from '../../utils/responsive';
import {ChatCategory} from '../../interfaces/api/auth/login';
import {updateConfig} from '../../screens/auth/login/actions';

const AnimatedTouchableOpacity =
  Animated.createAnimatedComponent(TouchableOpacity);

const ContentContainer = styled(Animated.View)`
  align-self: center;
  width: 100%;
  flex-grow: 1;
  border-radius: 16px;
  z-index: 99;
  shadow-color: ${({theme}) => theme.text.black};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  padding: ${({theme}) => theme.text.s4}px 0;
`;

const QuestionsContainer = styled.View`
  width: 100%;
  align-self: center;
`;

const QuestionContainer = styled(AnimatedTouchableOpacity)<{
  isSelected: boolean;
}>`
  width: 100%;
  align-self: center;
  background-color: ${({theme, isSelected}) =>
    isSelected ? theme.primary + '22' : theme.screenBackground};
  padding: ${({theme}) => theme.text.s6}px ${({theme}) => theme.text.s55}px;
  border-radius: 8px;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;

const QuestionTitle = styled(TextGeneric)<{
  isBold: boolean;
}>`
  color: ${({theme, disabled}) =>
    disabled ? theme.text.dark + 'BB' : theme.text.dark};
  font-size: ${({theme, isBold}) => isBold ? theme.text.s6 : theme.text.s6}px;
  flex: 1;
  padding-right: ${({theme}) => theme.text.s55}px;
  font-weight: ${({isBold}) => (isBold ? 'bold' : 'normal')};
`;

const IconContainer = styled.View`
  align-items: center;
  justify-content: center;
`;

const Separator = styled.View`
  width: 90%;
  align-self: center;
  height: 1px;
  background-color: ${({theme}) => theme.text.grey + '22'};
`;

const QuestionsTitle = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme, isBig}) => (isBig ? theme.text.s55 : theme.text.s6)}px;
  width: 100%;
  padding-left: ${({theme}) => theme.text.s8}px;
  align-self: center;
  padding-bottom: ${({theme}) => theme.text.s55}px
  ${({theme, isBig}) =>
    isBig &&
    `
    padding-top: ${theme.text.s55}px;
    padding-bottom: ${theme.text.s4}px;
    `};
`;

// Web-specific CSS animations for clinical case categories
const clinicalCaseCSSAnimations = Platform.OS === 'web' ? `
  @keyframes clinicalCaseFadeInUp {
    from {
      opacity: 0;
      transform: translateY(15px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes clinicalCaseFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .clinical-case-category-animate {
    animation: clinicalCaseFadeInUp 0.4s ease-out forwards;
    animation-fill-mode: both;
  }

  .clinical-case-container-animate {
    animation: clinicalCaseFadeIn 0.3s ease-out forwards;
    animation-fill-mode: both;
  }
` : '';

// Inject CSS styles for web
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  const styleId = 'clinical-case-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = clinicalCaseCSSAnimations;
    document.head.appendChild(style);
  }
}

export interface ClinicalCaseCategoriesProps {
  chat_categories?: ChatCategory[];
  onQuestionPress: (item: ChatCategory) => void;
  question?: string;
  showTitle?: boolean;
  containerStyle?: any;
  scrollViewStyle?: any;
  isLoading?: boolean;
  isNewChat?: boolean;
}

/**
 * ClinicalCaseCategories component for displaying clinical case categories.
 *
 * @returns {React.ReactElement} The ClinicalCaseCategories component.
 */
const ClinicalCaseCategories: React.FC<ClinicalCaseCategoriesProps> = ({
                                                                         chat_categories = [],
                                                                         onQuestionPress,
                                                                         question = '',
                                                                         showTitle = true,
                                                                         containerStyle,
                                                                         scrollViewStyle,
                                                                         isLoading = false,
                                                                         isNewChat = false,
                                                                       }): React.ReactElement => {
  const {hdp, getByScreenSize} = useResponsiveDimensions();
  const theme = useTheme() as Theme;
  const [selected, setSelected] = useState<any>();
  const {top: topInset, bottom: bottomInset} = useSafeAreaInsets();
  const headerHeight = useCustomHeaderHeight();


  const onQuestionPressHandler = (item: ChatCategory) => {
    setSelected(item);
    onQuestionPress(item);
  };

  const _renderQuestion = (item: ChatCategory, index: number) => {
    const questionContent = (
      <>
        <QuestionTitle isBold={index === chat_categories.length - 1 && item.title?.toLowerCase() === 'clinical case brief'} disabled={question?.length === 0}>
          {item.title || ''}
        </QuestionTitle>
        <IconContainer
          style={{
            opacity: question?.length === 0 ? 0.5 : 1,
          }}>
          <Icon
            name={'navArrow'}
            size={theme.text.s2}
            color={theme.text.dark + '11'}
            stroke={theme.text.dark}
          />
        </IconContainer>
      </>
    );

    return (
      <React.Fragment key={index}>
        {index !== 0 && <Separator />}

        {Platform.OS === 'web' ? (
          <div
            className="clinical-case-category-animate"
            style={{
              width: '100%',
              alignSelf: 'center' as const,
              backgroundColor: selected?.id === item.id ? theme.primary + '22' : theme.screenBackground,
              padding: `${theme.text.s6}px ${theme.text.s55}px`,
              borderRadius: '8px',
              display: 'flex' as const,
              alignItems: 'center' as const,
              justifyContent: 'space-between' as const,
              flexDirection: 'row' as const,
              cursor: question?.length === 0 ? 'not-allowed' : 'pointer',
              opacity: question?.length === 0 ? 0.8 : 1,
              animationDelay: `${index * 100}ms`,
            }}
            onClick={() => {
              if (question?.length > 0) {
                onQuestionPressHandler(item);
              }
            }}>
            {questionContent}
          </div>
        ) : (
          <QuestionContainer
            isSelected={selected?.id === item.id}
            activeOpacity={0.8}
            disabled={question?.length === 0}
            onPress={() => onQuestionPressHandler(item)}
            entering={FadeInDown.delay(index * 100)}>
            {questionContent}
          </QuestionContainer>
        )}
      </React.Fragment>
    );
  };

  return Platform.OS === 'web' ? (
    <div
      className="clinical-case-container-animate"
      style={{
        alignSelf: 'center' as const,
        width: '100%',
        flexGrow: 1,
        borderRadius: '16px',
        zIndex: 99,
        padding: `${theme.text.s4}px 0`,
        ...containerStyle,
      }}>
      <div style={{
        width: getByScreenSize('100%', '90%'),
        alignSelf: 'center' as const,
        marginLeft: isNewChat ? getByScreenSize('0%', '5%') : '0%',
      }}>
        {showTitle && (
          <div style={{
            color: theme.text.dark,
            fontSize: theme.text.s6,
            width: '100%',
            paddingLeft: theme.text.s8,
            alignSelf: 'center' as const,
            paddingBottom: theme.text.s55,
            fontWeight: 'bold' as const,
          }}>
            {tr('chat.clinicalCaseCategoryTitle') + ' '}
            <span style={{color: theme.text.error}}>*</span>
          </div>
        )}
        <div
          style={{
            borderTopRightRadius: '20px',
            borderTopLeftRadius: '20px',
            backgroundColor: theme.screenBackground,
            ...scrollViewStyle,
          }}>
          {isLoading ? (
            <div style={{
              minHeight: `${hdp(30)}px`,
              display: 'flex' as const,
              alignItems: 'center' as const,
              justifyContent: 'center' as const,
            }}>
              <SearchListSkeleton
                marginBottom={2}
                extraSmall
                borderRadius={0}
                numberOfItems={7}
                height={theme.text.s1 * 1.5}
              />
            </div>
          ) : chat_categories?.length === 0 ? (
            <div style={{
              minHeight: `${hdp(30)}px`,
              display: 'flex' as const,
              justifyContent: 'center' as const,
              alignItems: 'center' as const,
            }}>
              <EmptyList
                iconName="alert"
                iconColor={theme.text.grey}
                iconSize={theme.text.s1 * 2}
                title="No specialties available right now"
                titleStyle={{
                  color: theme.text.grey,
                  fontSize: theme.text.s6,
                  textAlign: 'center',
                }}
                showRefresh={false}
              />
            </div>
          ) : (
            chat_categories.map(_renderQuestion)
          )}
          <div style={{
            alignSelf: 'center' as const,
            width: '100%',
            height: `${(bottomInset || 0) + headerHeight + hdp(20)}px`,
          }} />
        </div>
      </div>
    </div>
  ) : (
    <ContentContainer style={containerStyle}>
      <QuestionsContainer
        style={{
          width: getByScreenSize('100%', '90%'),
          marginLeft: isNewChat ? '10%' : '0%',
        }}>
        {showTitle && (
          <QuestionsTitle bold>
            {tr('chat.clinicalCaseCategoryTitle') + ' '}
            <QuestionsTitle style={{color: theme.text.error}} bold>
              *
            </QuestionsTitle>
          </QuestionsTitle>
        )}
        <View
          style={[
            {
              borderTopRightRadius: 20,
              borderTopLeftRadius: 20,
              backgroundColor: theme.screenBackground,
            },
            scrollViewStyle,
          ]}>
          {isLoading ? (
            <EmptyContainer
              hdp={hdp}
              style={{
                minHeight: hdp(30),
              }}>
              <SearchListSkeleton
                marginBottom={2}
                extraSmall
                borderRadius={0}
                numberOfItems={7}
                height={theme.text.s1 * 1.5}
              />
            </EmptyContainer>
          ) : chat_categories?.length === 0 ? (
            <EmptyContainer
              hdp={hdp}
              style={{
                minHeight: hdp(30),
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <EmptyList
                iconName="alert"
                iconColor={theme.text.grey}
                iconSize={theme.text.s1 * 2}
                title="No specialties available right now"
                titleStyle={{
                  color: theme.text.grey,
                  fontSize: theme.text.s6,
                  textAlign: 'center',
                }}
                showRefresh={false}
              />
            </EmptyContainer>
          ) : (
            chat_categories.map(_renderQuestion)
          )}
          <Space
            bottomInset={bottomInset}
            extraSpace={headerHeight + hdp(20)}
          />
        </View>
      </QuestionsContainer>
    </ContentContainer>
  );
};

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, null)(ClinicalCaseCategories);
